/**
 * API client for JSJ Painting Quote System
 */

const API_URL = import.meta.env.DEV
    ? '/api'  // Proxied in dev
    : 'https://jsj-painting-api.jsj-painting.workers.dev/api';

let authToken = localStorage.getItem('auth_token');

export function setAuthToken(token) {
    authToken = token;
    if (token) {
        localStorage.setItem('auth_token', token);
    } else {
        localStorage.removeItem('auth_token');
    }
}

export function getAuthToken() {
    return authToken;
}

async function apiRequest(endpoint, options = {}) {
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers
    };

    if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers
    });

    if (response.status === 401) {
        // Unauthorized - clear token
        setAuthToken(null);
        window.location.href = '/';
        throw new Error('Unauthorized');
    }

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Request failed');
    }

    return response.json();
}

export async function login(password) {
    const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
    });

    const result = await response.json();

    if (result.success) {
        setAuthToken(result.token);
    }

    return result;
}

export async function submitQuote(quoteData, photos) {
    const formData = new FormData();
    formData.append('payload', JSON.stringify(quoteData));

    for (const photo of photos) {
        formData.append('photos', photo);
    }

    const response = await fetch(`${API_URL}/submit-quote`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${authToken}`
        },
        body: formData
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok || result.success === false) {
        throw new Error(result.error || result.message || 'Submit failed');
    }

    return result;
}

export async function getPendingQuotes() {
    return apiRequest('/quotes/pending');
}

export async function getQuote(quoteId) {
    return apiRequest(`/quotes/${quoteId}`);
}

export async function updateQuote(quoteId, updates) {
    return apiRequest(`/quotes/${quoteId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
    });
}

export async function approveQuote(quoteId, selectedPaintQuality, finalAdjustments) {
    return apiRequest(`/quotes/${quoteId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ selectedPaintQuality, finalAdjustments })
    });
}

export async function submitEstimate(payload) {
    const response = await fetch(`${API_URL}/estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok || result.success === false) {
        const errorMessage = result.error
            || result.message
            || (Array.isArray(result.errors) ? result.errors.join(' ') : null)
            || 'Estimate request failed';
        throw new Error(errorMessage);
    }

    return result;
}

export async function pollQuoteStatus(quoteId, maxAttempts = 30) {
    for (let i = 0; i < maxAttempts; i++) {
        const result = await getQuote(quoteId);

        if (result.quote.status !== 'processing') {
            return result.quote;
        }

        await new Promise(resolve => setTimeout(resolve, 2000));
    }

    throw new Error('Timeout waiting for quote processing');
}

export async function searchLead(email, phone) {
    const params = new URLSearchParams();
    if (email) params.append('email', email);
    if (phone) params.append('phone', phone);

    return apiRequest(`/leads/search?${params.toString()}`);
}
